import { makeStyles } from "tss-react/mui";
import {
  FILE_MIME_TYPES,
  VALID_DOCUMENT_VIEWER_FILE_MIME_TYPES,
  VIDEO_FILE_MIME_TYPES,
  WORD_MIME_TYPES,
} from "@miplatform/constants";
import { IDocument } from "@miplatform/types";
import { getRootUrl } from "@miplatform/utils";
import {
  mdiFilePdfBox,
  mdiFileWordBox,
  mdiImageOffOutline,
  mdiPlay,
  mdiTextBox,
  mdiTrashCanOutline,
} from "@mdi/js";
import { Box, useTheme } from "@mui/material";
import { MiIcon } from "../MiIcon/MiIcon";

type DocumentItem = IDocument & { tempURL?: string };

interface DocumentProps {
  document?: DocumentItem;
  classes: { [index: string]: string };
}

const Document = ({ document, classes }: DocumentProps) => {
  const theme = useTheme();
  const API_URL = getRootUrl();

  if (!document?.referenceId && !document?.tempURL) {
    return (
      <Box className={classes.icon}>
        <MiIcon
          path={mdiImageOffOutline}
          size={1}
          color={theme.palette.grey.main}
        />
      </Box>
    );
  }

  if (FILE_MIME_TYPES.PDF === document?.contentType) {
    return (
      <Box className={classes.icon}>
        <MiIcon
          path={mdiFilePdfBox}
          size={5}
          color={theme.palette.common.white}
        />
      </Box>
    );
  }

  if (WORD_MIME_TYPES.includes(document?.contentType)) {
    return (
      <Box className={classes.icon}>
        <MiIcon
          path={mdiFileWordBox}
          size={5}
          color={theme.palette.common.white}
        />
      </Box>
    );
  }

  if (VIDEO_FILE_MIME_TYPES.includes(document?.contentType)) {
    return (
      <Box width="100%" height="100%">
        <MiIcon
          path={mdiPlay}
          color={theme.palette.grey.main}
          size={1.5}
          className={classes.videoIcon}
        />
        <video
          src={`${API_URL}/${document.referenceId}`}
          className={classes.document}
        >
          <track kind="captions" />
        </video>
      </Box>
    );
  }

  if (!VALID_DOCUMENT_VIEWER_FILE_MIME_TYPES.includes(document?.contentType)) {
    return (
      <Box className={classes.icon}>
        <MiIcon path={mdiTextBox} size={5} color={theme.palette.common.white} />
      </Box>
    );
  }

  return (
    <img
      src={
        document?.referenceId
          ? `${API_URL}/${document.referenceId}`
          : document?.tempURL
      }
      className={classes.document}
      alt=""
    />
  );
};

interface DocumentThumbnailProps {
  document?: DocumentItem;
  documentCount: number;
  onClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
  onDelete?: () => void;
  dimension?: number;
  aspectRatio?: number;
  className?: string;
  style?: React.CSSProperties;
}

export const DocumentThumbnail = ({
  document,
  documentCount,
  onClick,
  onDelete,
  dimension,
  aspectRatio,
  className,
  style,
}: DocumentThumbnailProps) => {
  const { theme, classes } = useStyles({ dimension, aspectRatio });

  return (
    <Box
      key={document?.referenceId || document?.tempURL}
      onClick={onClick}
      style={{ position: "relative" }}
    >
      {documentCount > 1 && (
        <Box className={classes.badgeStyles}>{documentCount}</Box>
      )}
      <Box
        className={`${classes.documentContainer} ${
          onClick ? classes.clickable : ""
        } ${className || ""}`}
        style={style}
      >
        <Document classes={classes} document={document} />
        {onDelete && (
          <Box className={classes.deleteIcon} onClick={onDelete}>
            <MiIcon
              path={mdiTrashCanOutline}
              color={theme.palette.error.main}
            />
          </Box>
        )}
      </Box>
    </Box>
  );
};

const useStyles = makeStyles<{
  dimension?: number;
  aspectRatio?: number;
}>()((theme, { dimension = 40, aspectRatio = 1 }) => ({
  documentContainer: {
    width: dimension,
    aspectRatio,
    height: "auto",
    position: "relative",
    border: `1px solid ${theme.palette.grey.main}`,
    borderRadius: 6,
    display: "grid",
    placeContent: "center",
    overflow: "hidden",
  },
  videoIcon: {
    position: "absolute",
    top: 2,
    left: 1,
    zIndex: theme.zIndex.base,
    opacity: 0.5,
  },
  deleteIcon: {
    position: "absolute",
    bottom: 0,
    right: 0,
    cursor: "pointer",
  },
  icon: {
    display: "grid",
    placeContent: "center",
  },
  document: {
    objectFit: "cover",
    width: "100%",
    height: "100%",
  },
  clickable: {
    cursor: "pointer",
  },
  badgeStyles: {
    position: "absolute",
    backgroundColor: theme.palette.primary.main,
    borderRadius: "50%",
    fontSize: 12,
    right: -5,
    top: -5,
    display: "flex",
    color: theme.palette.common.white,
    width: "18px",
    height: "18px",
    justifyContent: "center",
    alignItems: "center",
    zIndex: theme.zIndex.base,
  },
}));
